import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { Target, Clock, Settings as SettingsIcon, LogOut, Plus, LogIn, Timer, Camera, ChevronRight, Swords, TrendingUp, Users } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { resolveAvatar } from "../lib/avatars";
import DelayCamModal from "../components/DelayCamModal";
import api from "../lib/api";
import SocialPanel from "../components/SocialPanel";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [delayCamOpen, setDelayCamOpen] = useState(false);
  const [recent, setRecent] = useState(null);
  const [socialOpen, setSocialOpen] = useState(false);
  const [socialCount, setSocialCount] = useState(0);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const [r, i] = await Promise.all([api.get("/friends/requests"), api.get("/invitations")]);
        if (alive) setSocialCount((r.data.incoming?.length || 0) + (i.data.invitations?.length || 0));
      } catch { /* silencieux */ }
    };
    poll();
    const iv = setInterval(poll, 10000);
    const onVis = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      alive = false;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, []);

  useEffect(() => {
    let alive = true;
    api.get("/history").then(({ data }) => {
      if (!alive) return;
      const items = data.items || [];
      if (!items.length) return;
      const dayAgo = Date.now() - 24 * 3600 * 1000;
      const activeRecent = items.find(
        (i) => i.status !== "finished" && new Date(i.created_at).getTime() >= dayAgo
      );
      setRecent(activeRecent || items[0]);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const openRecent = () => {
    if (!recent) return;
    navigate(recent.type === "match" ? `/match/${recent.id}` : `/solo/${recent.id}`);
  };

  const fmtWhen = (iso) => {
    try {
      const d = new Date(iso);
      const mins = Math.round((Date.now() - d.getTime()) / 60000);
      if (mins < 60) return `${Math.max(1, mins)} min`;
      if (mins < 1440) return `${Math.floor(mins / 60)} h`;
      return d.toLocaleDateString();
    } catch { return ""; }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] grain text-white">
      <header className="sticky top-0 z-20 glass-header">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center" data-testid="brand-link">
            <img src="/bowleague-logo.svg" alt="Bow League" className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-2">
            <button data-testid="nav-social" onClick={() => setSocialOpen(true)}
              className="relative text-zinc-400 hover:text-white p-2 rounded-md hover:bg-zinc-900 transition-colors">
              <Users className="h-5 w-5" />
              {socialCount > 0 && (
                <span className="absolute top-1 right-1 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-[#0A0A0A]" />
              )}
            </button>
            <Link to="/settings" data-testid="nav-settings" className="flex items-center gap-2 pl-3 pr-2 py-1.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded-full transition-colors">
              <div className="h-7 w-7 rounded-full bg-zinc-800 overflow-hidden flex items-center justify-center">
                <img src={resolveAvatar(user)} alt="" className="h-full w-full object-cover" />
              </div>
              <span className="text-sm font-medium">{user?.nickname || user?.name}</span>
              <SettingsIcon className="h-4 w-4 text-zinc-500 ml-1" />
            </Link>
            <button data-testid="nav-logout" onClick={logout} className="text-zinc-400 hover:text-white p-2 rounded-md hover:bg-zinc-900 transition-colors">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-8 pt-6 md:pt-8 pb-12">
        <div className="mb-6">
          <div className="text-cyan-400 text-xs uppercase tracking-[0.3em] mb-3">{t("dashboard.control_room")}</div>
          <h1 data-testid="dashboard-title" className="font-heading font-black text-5xl sm:text-6xl uppercase tracking-tight">
            {t("dashboard.welcome")} <span className="text-cyan-400">{user?.nickname || user?.name}</span>
          </h1>
        </div>

        {recent && (
          <button
            data-testid="tile-recent"
            onClick={openRecent}
            className="w-full mb-4 flex items-center gap-3 glass-tile px-4 py-3 text-left transition-all"
          >
            {recent.type === "match"
              ? <Swords className="h-5 w-5 text-cyan-400 shrink-0" strokeWidth={2} />
              : <TrendingUp className="h-5 w-5 text-cyan-400 shrink-0" strokeWidth={2} />}
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                {recent.status !== "finished" ? t("dashboard.resume") : t("dashboard.last_session")}
              </div>
              <div className="font-bold truncate text-sm">
                {recent.session_name || t(`format.${recent.format}`)}
                <span className="text-zinc-600 font-normal"> · {fmtWhen(recent.created_at)}</span>
              </div>
            </div>
            {recent.type === "solo" && (
              <span className="font-heading font-black text-xl text-cyan-400 tabular-nums shrink-0">{recent.score}</span>
            )}
            <ChevronRight className="h-4 w-4 text-zinc-600 shrink-0" />
          </button>
        )}

        <div className="grid grid-cols-2 gap-3 md:gap-5">
          <button
            data-testid="tile-solo"
            onClick={() => navigate("/solo/setup")}
            style={{ ["--glow"]: "253 224 71" }}
            className="flex flex-col items-start text-left glass-tile p-6 min-h-[135px] transition-all"
          >
            <Target className="h-6 w-6 text-[#FDE047] mb-3" strokeWidth={2} />
            <h3 className="font-heading font-black text-3xl uppercase tracking-tight">{t("dashboard.tile_solo_title")}</h3>
          </button>

          <button
            data-testid="tile-host-match"
            onClick={() => navigate("/match/create")}
            style={{ ["--glow"]: "239 68 68" }}
            className="flex flex-col items-start text-left glass-tile p-6 min-h-[135px] transition-all"
          >
            <Plus className="h-6 w-6 text-red-500 mb-3" strokeWidth={2} />
            <h3 className="font-heading font-black text-3xl uppercase tracking-tight">{t("dashboard.tile_host")}</h3>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-1">{t("dashboard.tile_host_sub")}</div>
          </button>

          <button
            data-testid="tile-join-match"
            onClick={() => navigate("/match/join")}
            style={{ ["--glow"]: "96 165 250" }}
            className="flex flex-col items-start text-left glass-tile p-6 min-h-[135px] transition-all"
          >
            <LogIn className="h-6 w-6 text-blue-400 mb-3" strokeWidth={2} />
            <h3 className="font-heading font-black text-3xl uppercase tracking-tight">{t("dashboard.tile_join_title")}</h3>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-1">{t("dashboard.tile_join_label")}</div>
          </button>

          <button
            data-testid="tile-history"
            onClick={() => navigate("/history")}
            style={{ ["--glow"]: "34 211 238" }}
            className="flex flex-col items-start text-left glass-tile p-6 min-h-[135px] transition-all"
          >
            <Clock className="h-6 w-6 text-white mb-3" strokeWidth={2} />
            <h3 className="font-heading font-black text-3xl uppercase tracking-tight">{t("dashboard.tile_history_title")}</h3>
          </button>

          <button
            data-testid="tile-chrono"
            onClick={() => navigate("/chrono")}
            style={{ ["--glow"]: "34 211 238" }}
            className="flex flex-col items-start text-left glass-tile p-6 min-h-[135px] transition-all"
          >
            <Timer className="h-6 w-6 text-white mb-3" strokeWidth={2} />
            <h3 className="font-heading font-black text-3xl uppercase tracking-tight">{t("chrono.tile_title")}</h3>
          </button>

          <button
            data-testid="tile-delaycam"
            onClick={() => setDelayCamOpen(true)}
            style={{ ["--glow"]: "244 114 182" }}
            className="flex flex-col items-start text-left glass-tile p-6 min-h-[135px] transition-all"
          >
            <Camera className="h-6 w-6 text-pink-400 mb-3" strokeWidth={2} />
            <h3 className="font-heading font-black text-3xl uppercase tracking-tight">{t("delaycam.tile_title")}</h3>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-1">{t("delaycam.tile_desc")}</div>
          </button>
        </div>
        <div className="mt-12 text-center">
          <button onClick={() => navigate("/legal")} className="text-[11px] uppercase tracking-[0.2em] text-zinc-600 hover:text-zinc-400 transition-colors">
            {t("dashboard.tile_legal_label")}
          </button>
        </div>
      </main>
      {delayCamOpen && createPortal(<DelayCamModal onClose={() => setDelayCamOpen(false)} />, document.body)}
      <SocialPanel open={socialOpen} onClose={() => setSocialOpen(false)} onCountChange={setSocialCount} />
    </div>
  );
}
